import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { checkTenantDomainAPI, createTenantAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import { platformUrl } from '../../../utils/mhNav';
import styles from './CreateTenantWizard.module.scss';

const DRAFT_KEY = 'mh-tenant-wizard-draft';

const TIMEZONES = ['Asia/Muscat', 'Asia/Dubai', 'Asia/Riyadh', 'Europe/London', 'America/New_York'].map((z) => ({
  value: z,
  label: z,
}));

const LANGS = [
  { value: 'en', label: 'English' },
  { value: 'ar', label: 'Arabic' },
  { value: 'fr', label: 'French' },
];

const defaultValues = {
  companyName: '',
  domain: '',
  industry: '',
  timezone: null,
  region: '',
  plan: 'Growth',
  userCap: 50,
  meetingQuota: 500,
  aiTokenQuota: 500000,
  storageGb: 25,
  billingCycle: 'monthly',
  ownerFirst: '',
  ownerLast: '',
  ownerEmail: '',
  sendInvite: true,
  primaryColor: '#644998',
  botName: 'MeetingHub Bot',
  defaultLanguage: LANGS[0],
  logoFile: null,
};

const STEP_FIELDS = [
  ['companyName', 'domain', 'industry', 'timezone', 'region'],
  ['plan', 'userCap', 'meetingQuota', 'aiTokenQuota', 'storageGb', 'billingCycle'],
  ['ownerFirst', 'ownerLast', 'ownerEmail'],
  ['primaryColor', 'botName', 'defaultLanguage'],
];

export default function CreateTenantWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [logoPreview, setLogoPreview] = useState(null);
  const [previewCode, setPreviewCode] = useState(() => `T-${String(Math.floor(10000 + Math.random() * 89999))}`);

  const {
    register,
    control,
    watch,
    trigger,
    getValues,
    setValue,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({ defaultValues, mode: 'onTouched' });

  const companyName = watch('companyName');
  const plan = watch('plan');
  const domain = watch('domain');

  useEffect(() => {
    if (companyName) {
      setPreviewCode(`T-${String((companyName.length * 9301 + 49297) % 90000 + 10000)}`);
    }
  }, [companyName]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.defaultLanguage && typeof d.defaultLanguage === 'object') reset({ ...defaultValues, ...d });
        else reset({ ...defaultValues, ...d });
      }
    } catch {
      /* ignore */
    }
  }, [reset]);

  const saveDraft = () => {
    const v = getValues();
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...v, timezone: v.timezone, defaultLanguage: v.defaultLanguage }));
    toast.success('Draft saved');
  };

  const validateDomain = async () => {
    const d = getValues('domain');
    if (!d) return false;
    try {
      const res = await checkTenantDomainAPI(d);
      if (!res.data.available) {
        setError('domain', { type: 'validate', message: 'Domain already exists' });
        return false;
      }
      clearErrors('domain');
      return true;
    } catch {
      toast.error('Could not verify domain');
      return false;
    }
  };

  const goNext = async () => {
    const fields = STEP_FIELDS[step];
    const ok = await trigger(fields);
    if (!ok) return;
    if (step === 0) {
      const domOk = await validateDomain();
      if (!domOk) return;
    }
    setStep((s) => Math.min(s + 1, 4));
  };

  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const portalBase = useMemo(() => {
    const dom = (domain || 'your-domain').replace(/^https?:\/\//, '');
    return `https://${dom || 'tenant'}.app.meetinghub.ai`;
  }, [domain]);

  const onCreate = async () => {
    const v = getValues();
    try {
      const res = await createTenantAPI({
        companyName: v.companyName,
        domain: v.domain,
        plan: v.plan,
        owner: `${v.ownerFirst} ${v.ownerLast}`,
        ownerEmail: v.ownerEmail,
      });
      toast.success('Tenant created');
      localStorage.removeItem(DRAFT_KEY);
      navigate(platformUrl(`tenants/${res.data.id}`));
    } catch (e) {
      toast.error(e.message || 'Create failed');
    }
  };

  const stepsUi = ['Basic', 'Plan', 'Owner', 'Branding', 'Review'];

  return (
    <div>
      <PageHeader
        title="Create Tenant"
        breadcrumbs={[
          { label: 'Platform', path: platformUrl('dashboard') },
          { label: 'Tenants', path: platformUrl('tenants') },
          { label: 'Create' },
        ]}
        secondaryActions={[{ label: 'Save Draft', onClick: saveDraft }]}
      />

      <div className={`${styles.stepper} d-flex flex-wrap gap-2 mb-4`}>
        {stepsUi.map((label, i) => (
          <button
            key={label}
            type="button"
            className={`${styles.stepPill} ${i === step ? styles.stepActive : ''} ${i < step ? styles.stepDone : ''}`}
            onClick={() => {
              if (i < step) setStep(i);
            }}
            disabled={i > step}
          >
            {i + 1}. {label}
          </button>
        ))}
      </div>

      <Card className="mb-3">
        <Card.Body>
          {step === 0 && (
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Company Name *</Form.Label>
                  <Form.Control {...register('companyName', { required: 'Required' })} isInvalid={!!errors.companyName} />
                  <Form.Control.Feedback type="invalid">{errors.companyName?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Domain *</Form.Label>
                  <Form.Control
                    {...register('domain', { required: 'Required' })}
                    placeholder="company.meetinghub.ai"
                    isInvalid={!!errors.domain}
                  />
                  <Form.Control.Feedback type="invalid">{errors.domain?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Industry</Form.Label>
                  <Form.Select {...register('industry', { required: 'Required' })} isInvalid={!!errors.industry}>
                    <option value="">Select…</option>
                    <option value="Telecom">Telecom</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Finance">Finance</option>
                    <option value="Government">Government</option>
                    <option value="Other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Label>Timezone</Form.Label>
                <Controller
                  name="timezone"
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field }) => (
                    <Select options={TIMEZONES} value={field.value} onChange={field.onChange} placeholder="Search…" />
                  )}
                />
                {errors.timezone && <div className="text-danger small mt-1">Required</div>}
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Region</Form.Label>
                  <Form.Select {...register('region', { required: 'Required' })}>
                    <option value="">Select…</option>
                    <option value="ME">ME</option>
                    <option value="APAC">APAC</option>
                    <option value="EU">EU</option>
                    <option value="US">US</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          )}

          {step === 1 && (
            <Row className="g-3">
              <Col xs={12}>
                <Form.Label>Plan</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {['Starter', 'Growth', 'Enterprise', 'Custom'].map((p) => (
                    <Form.Check
                      key={p}
                      type="radio"
                      id={`plan-${p}`}
                      label={p}
                      checked={plan === p}
                      onChange={() => setValue('plan', p, { shouldValidate: true })}
                    />
                  ))}
                </div>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>User cap</Form.Label>
                  <Form.Control
                    type="number"
                    {...register('userCap', { valueAsNumber: true })}
                    disabled={plan === 'Enterprise'}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Meeting quota</Form.Label>
                  <Form.Control type="number" {...register('meetingQuota', { valueAsNumber: true })} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>AI token quota</Form.Label>
                  <Form.Control type="number" {...register('aiTokenQuota', { valueAsNumber: true })} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Storage (GB)</Form.Label>
                  <Form.Control type="number" {...register('storageGb', { valueAsNumber: true })} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Label>Billing cycle</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    label="Monthly"
                    name="billingCycle"
                    checked={watch('billingCycle') === 'monthly'}
                    onChange={() => setValue('billingCycle', 'monthly')}
                  />
                  <Form.Check
                    type="radio"
                    label="Annual"
                    name="billingCycle"
                    checked={watch('billingCycle') === 'annual'}
                    onChange={() => setValue('billingCycle', 'annual')}
                  />
                </div>
              </Col>
            </Row>
          )}

          {step === 2 && (
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>First name *</Form.Label>
                  <Form.Control {...register('ownerFirst', { required: true })} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Last name *</Form.Label>
                  <Form.Control {...register('ownerLast', { required: true })} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Email *</Form.Label>
                  <Form.Control type="email" {...register('ownerEmail', { required: true })} />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Check label="Send invite email" {...register('sendInvite')} />
              </Col>
              <Col xs={12}>
                <Form.Label>Role</Form.Label>
                <Form.Control readOnly value="Tenant Admin" />
              </Col>
            </Row>
          )}

          {step === 3 && (
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Primary color</Form.Label>
                  <Form.Control type="color" {...register('primaryColor')} />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group>
                  <Form.Label>Logo</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        setLogoPreview(URL.createObjectURL(f));
                        setValue('logoFile', f.name);
                      }
                    }}
                  />
                  {logoPreview && <img src={logoPreview} alt="" className={`${styles.logoPrev} mt-2 rounded`} />}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Bot display name</Form.Label>
                  <Form.Control {...register('botName')} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Label>Default language</Form.Label>
                <Controller
                  name="defaultLanguage"
                  control={control}
                  render={({ field }) => <Select options={LANGS} value={field.value} onChange={field.onChange} />}
                />
              </Col>
            </Row>
          )}

          {step === 4 && (
            <div className="small">
              <h6>Summary</h6>
              <p>
                <strong>Company:</strong> {getValues('companyName')} · <strong>Domain:</strong> {getValues('domain')}
              </p>
              <p>
                <strong>Plan:</strong> {getValues('plan')} · <strong>Region:</strong> {getValues('region')}
              </p>
              <p>
                <strong>Owner:</strong> {getValues('ownerFirst')} {getValues('ownerLast')} ({getValues('ownerEmail')})
              </p>
              <p>
                <strong>Generated Tenant Code:</strong> {previewCode}
              </p>
              <Alert variant="light" className="border">
                <strong>Default URLs</strong>
                <div>Company Admin: {portalBase}/company</div>
                <div>Platform (internal): {platformUrl('dashboard')}</div>
              </Alert>
              <h6 className="mt-3">Onboarding preview</h6>
              <ul className="text-muted">
                <li>Connect Microsoft — pending</li>
                <li>Connect Google — pending</li>
                <li>Connect WhatsApp — pending</li>
                <li>Upload Teams App — pending</li>
                <li>Test Booking — pending</li>
              </ul>
            </div>
          )}
        </Card.Body>
      </Card>

      <div className="d-flex gap-2 flex-wrap">
        <Button variant="outline-secondary" onClick={goBack} disabled={step === 0}>
          Back
        </Button>
        {step < 4 && (
          <Button variant="primary" onClick={goNext}>
            Next
          </Button>
        )}
        {step === 4 && (
          <Button variant="success" onClick={onCreate}>
            Create Tenant
          </Button>
        )}
      </div>
    </div>
  );
}
